import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import getImage from '../utils/getImage';
import getNoteByChapterKey from '../utils/getNoteByChapterKey';
import { editWhite } from '../utils/icons';
import apiRequest from '../utils/apiRequest';
import renderSectionContent from '../utils/renderSectionContent';
import ErrorBoundary from './ErrorBoundary';
import NavBar from './NavBar';
import Logo from './Logo';
import {
  TinyText,
  TinyTitle,
  TinyLink,
  Line,
  BaseLink
} from './CommonUiComponents';
import ChapterNavigation from './ChapterNavigation';
import Footer from './Footer';
import ActionExpander from './ActionExpander';
import Note from './Note';
import SectionNavigation from '../containers/SectionNavigationContainer';
import StatusLoader from './StatusLoader';

const ChapterCount = styled.p`
  font-size: 0.875rem;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 3px;
  letter-spacing: 1px;
`;

const TitleSection = styled.div`
  margin-top: 5rem;

  @media screen and (max-width: 64rem) {
    margin-top: 6rem;
    margin-bottom: 2rem;
    padding: 0 1.5rem;
  }
`;

const StyledTitle = styled.h1`
  font-size: 2.625rem;
  margin-top: 0;

  @media screen and (max-width: 64rem) {
    margin-bottom: 0;
  }

  @media screen and (max-width: 40rem) {
    font-size: 2rem;
  }
`;

const Ingress = styled.div`
  font-size: 1.45rem;
  font-family: 'Source Serif Pro';
  line-height: 1.5em;
  max-width: 35.625rem;

  a {
    color: ${props => props.theme.secondary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  p {
    font-weight: 700;
  }

  ${props =>
    props.noSpacing &&
    css`
      margin-bottom: 1rem;
      max-width: 35.625rem;

      > p:last-child {
        margin-bottom: 0;
      }
    `};

  @media screen and (max-width: 64rem) {
    padding: 0 1.5rem;
    margin: 0 auto;
  }
`;

const Section = styled.div`
  display: flex;
  margin: 1.5rem 0 0 5rem;
  overflow: hidden;

  @media screen and (max-width: 64rem) {
    flex-direction: column-reverse;
    margin: 0;
  }

  ${props =>
    props.top &&
    css`
      margin: 3.7rem 0 0 5rem;
    `};
`;

const StyledFeaturedGFX = styled.div`
  ${props =>
    props.video &&
    css`
      position: relative;
      padding-bottom: 56.25%; /* 16:9 */
      padding-top: 25px;

      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    `};

  ${props =>
    props.image &&
    css`
      margin: 0 auto;
    `};

  img {
    width: 100%;
  }

  @media all and (max-width: 1474px) {
    margin: 0 auto;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${props => props.alignment || 'flex-start'};
  flex: 1;
  max-width: 23.125rem;
  padding: 0 2rem 0 0;
  width: 20vw;

  @media screen and (max-width: 64rem) {
    margin: 0 auto;
    width: auto;
    display: block;
    padding: 0 1.5rem;
    max-width: 35.625rem;

    ${props =>
      props.empty &&
      css`
        margin: 0 auto;
      `};
  }
`;

const Content = styled.div`
  flex: 2;
  overflow: hidden;
`;

const FeaturedGFX = ({ video, image }) => {
  if (video) {
    return (
      <StyledFeaturedGFX
        video={true}
        dangerouslySetInnerHTML={{ __html: video }}
      />
    );
  }

  return (
    <StyledFeaturedGFX>
      <img src={image} alt="" />
    </StyledFeaturedGFX>
  );
};

function renderChapters(chapters, currentChapter) {
  return chapters.map((c, k) => (
    <TinyLink key={c.key} to={c.slug} active={c.key === currentChapter}>
      Kap. {`${k + 1}`.padStart(2, '0')}
    </TinyLink>
  ));
}

export default class Chapter extends Component {
  state = {
    data: null,
    loading: false,
    error: false
  };

  componentDidMount() {
    const {
      setIsOnWhitePage,
      fetchNotes,
      match: {
        params: { chapterId }
      }
    } = this.props;

    window.scroll(0, 0);
    this.fetchData(chapterId);
    setIsOnWhitePage();
    document.body.className = 'body-reset';
    fetchNotes();
  }

  componentWillUnmount() {
    const { unsetIsOnWhitePage } = this.props;
    unsetIsOnWhitePage();
    document.body.className = '';
  }

  fetchData = async chapterSlug => {
    const {
      fetchBookIfNeeded,
      match: {
        params: { bookId }
      }
    } = this.props;

    const response = await apiRequest(
      'wordpress',
      `bookdata/v1/chapters/?slug=${chapterSlug}`
    );

    this.setState({ data: response });
    fetchBookIfNeeded(bookId);
  };

  renderSections(data) {
    if (!data.sections || !Array.isArray(data.sections)) return null;

    return data.sections.map((s, k) => {
      const sidebarContent = s.sidebar ? s.sidebar_content : null;

      return (
        <Section key={k} className="section">
          {!s.fullwidth && (
            <Sidebar
              empty={!s.sidebar}
              alignment={
                s.sidebar ? sidebarContent[0].alignement : 'flex-start'
              }
            >
              {s.sidebar &&
                sidebarContent.map((sc, j) =>
                  renderSectionContent(sc, j, data.sections, data)
                )}
            </Sidebar>
          )}
          <Content>
            {s !== null && renderSectionContent(s, k, data.sections, data)}
          </Content>
        </Section>
      );
    });
  }

  render() {
    const { notes, fetchNotes, bookId } = this.props;
    const { data } = this.state;

    if (!data) return <StatusLoader busy={true} fullsize={true} large={true} />;

    const activeNote = getNoteByChapterKey(notes, data.chapter);
    const currentPart = data.parts.find(p => p.key === data.part);
    const currentChapterIndex = currentPart.chapters.findIndex(
      c => c.key === data.chapter
    );

    return (
      <ErrorBoundary>
        <NavBar secondary>
          <TinyTitle>Del {currentPart.title}</TinyTitle>
          <Line />
          {renderChapters(currentPart.chapters, data.chapter)}
        </NavBar>
        <NavBar primary>
          <BaseLink to={`/bok/${bookId}`}>
            <Logo size="4.1875rem" />
          </BaseLink>
          <TinyTitle>{data.bookCategory}</TinyTitle>
          <TinyText italic>«{data.bookTitle}»</TinyText>
        </NavBar>
        <SectionNavigation />
        <Section>
          <Sidebar empty />
          <Content>
            <TitleSection>
              <ChapterCount>
                Del {currentPart.title}: Kapittel {currentChapterIndex + 1} av{' '}
                {currentPart.chapters.length}
              </ChapterCount>
              <StyledTitle>{data.title}</StyledTitle>
            </TitleSection>
          </Content>
        </Section>
        <Section>
          <Sidebar empty />
          <FeaturedGFX
            video={data.video}
            image={getImage(data.featuredImage, window.innerWidth)}
          />
        </Section>
        <Section>
          <Sidebar empty />
          <Content>
            <Ingress dangerouslySetInnerHTML={{ __html: data.intro }} />
          </Content>
        </Section>
        {this.renderSections(data)}
        <Section>
          <Sidebar empty />
          <Content>
            <ChapterNavigation
              chapters={data.chapters}
              currentChapter={data.chapter}
              parts={data.parts}
              currentPart={data.part}
              bookId={bookId}
            />
          </Content>
        </Section>
        <Footer bookId={bookId} />
        <ActionExpander label="Notat" icon={editWhite}>
          <Note
            chapterKey={data.chapter}
            fetchNotes={fetchNotes}
            data={activeNote}
          />
        </ActionExpander>
      </ErrorBoundary>
    );
  }
}

export { Section, Sidebar, Content, StyledTitle, Ingress };



// WEBPACK FOOTER //
// ./src/components/Chapter.js